import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import type { PortalEditorView } from '../../../lib/api';
import { FIELD, LABEL, PRIMARY, QUIET, Refusals, type SaveChange } from './editorShared';

type Item = PortalEditorView['openItems'][number];
interface ItemFields {
  item: string;
  why: string;
  owner: string;
  dealId: string | null;
}

/**
 * VP9 - the open items: add, edit, close. Closing takes a second, NAMED click, because it takes the item off the
 * client's page (it stays in the contract with its notes, and Undo brings it back). A closed item is counted here and
 * listed in the history, never on the page.
 */
export default function OpenItemsEditor({ view, onSave }: { view: PortalEditorView; onSave: SaveChange }) {
  const open = view.openItems.filter((o) => o.id && !o.closedAt);
  const closed = view.openItems.filter((o) => o.closedAt).length;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">
        Open items{' '}
        <span className="font-normal text-gray-500">
          ({open.length} open{closed ? `, ${closed} closed` : ''})
        </span>
      </h3>
      {open.length ? (
        <ul className="space-y-2">
          {open.map((o) => (
            <OpenItemRow key={o.id} item={o} view={view} onSave={onSave} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">No open items.</p>
      )}
      <ItemForm
        ariaLabel="Add an open item"
        submitLabel="Add item"
        view={view}
        initial={{ item: '', why: '', owner: '', dealId: null }}
        clearOnSave
        onSubmit={(fields) => onSave({ kind: 'openItemAdd', fields: { ...fields } })}
      />
    </div>
  );
}

function OpenItemRow({ item, view, onSave }: { item: Item; view: PortalEditorView; onSave: SaveChange }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const dealName = view.deals.find((d) => d.id === item.dealId)?.name ?? null;

  const close = async () => {
    setBusy(true);
    const refused = await onSave({ kind: 'openItemClose', target: item.id as string });
    setBusy(false);
    if (refused) {
      setErrors(refused);
    } else {
      setErrors([]);
      setConfirming(false);
    }
  };

  if (editing) {
    return (
      <li className="border border-gray-200 rounded-lg p-3">
        <ItemForm
          ariaLabel="Edit open item"
          submitLabel="Save item"
          view={view}
          initial={{ item: item.item ?? '', why: item.why ?? '', owner: item.owner ?? '', dealId: item.dealId }}
          onCancel={() => setEditing(false)}
          onSubmit={async (fields) => {
            const refused = await onSave({ kind: 'openItemEdit', target: item.id as string, fields: { ...fields } });
            if (!refused) setEditing(false);
            return refused;
          }}
        />
      </li>
    );
  }

  return (
    <li className="border border-gray-200 rounded-lg p-3">
      <p className="text-sm text-gray-900">{item.item}</p>
      {item.why && <p className="text-xs text-gray-500">{item.why}</p>}
      <p className="text-xs text-gray-500">{[item.owner ? `Owner: ${item.owner}` : '', dealName ?? ''].filter(Boolean).join(' - ')}</p>
      <div className="flex flex-wrap gap-2 mt-2">
        <button
          type="button"
          className={QUIET}
          onClick={() => {
            setEditing(true);
            setConfirming(false);
            setErrors([]);
          }}
        >
          Edit
        </button>
        {confirming ? (
          <>
            <button
              type="button"
              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50"
              disabled={busy}
              onClick={() => void close()}
            >
              Confirm close - it leaves the client's page; Undo brings it back
            </button>
            <button type="button" className={QUIET} onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className={QUIET} onClick={() => setConfirming(true)}>
            Close
          </button>
        )}
      </div>
      <Refusals errors={errors} />
    </li>
  );
}

function ItemForm({
  ariaLabel,
  submitLabel,
  view,
  initial,
  onSubmit,
  onCancel,
  clearOnSave = false,
}: {
  ariaLabel: string;
  submitLabel: string;
  view: PortalEditorView;
  initial: ItemFields;
  onSubmit: (fields: ItemFields) => Promise<string[] | null>;
  onCancel?: () => void;
  clearOnSave?: boolean;
}) {
  const id = useId();
  const [fields, setFields] = useState<ItemFields>(initial);
  const [errors, setErrors] = useState<string[]>([]);
  const [saved, setSaved] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key: keyof ItemFields) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFields((f) => ({ ...f, [key]: key === 'dealId' ? e.target.value || null : e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const refused = await onSubmit(fields);
    setSaving(false);
    if (refused) {
      setErrors(refused);
      setSaved('');
      return;
    }
    setErrors([]);
    if (clearOnSave) {
      setFields(initial);
      setSaved('Added - the client will see it under Open items, marked New.');
    }
  };

  return (
    <form onSubmit={submit} aria-label={ariaLabel} className="space-y-2">
      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{ariaLabel}</h4>
      <div>
        <label className={LABEL} htmlFor={`${id}-item`}>
          The open item
        </label>
        <input id={`${id}-item`} value={fields.item} onChange={set('item')} maxLength={200} className={FIELD} />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className={LABEL} htmlFor={`${id}-why`}>
            Why
          </label>
          <input id={`${id}-why`} value={fields.why} onChange={set('why')} maxLength={300} className={FIELD} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`${id}-owner`}>
            Owner
          </label>
          <input id={`${id}-owner`} value={fields.owner} onChange={set('owner')} maxLength={60} placeholder="e.g. Seller" className={FIELD} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`${id}-deal`}>
            Deal
          </label>
          <select id={`${id}-deal`} value={fields.dealId ?? ''} onChange={set('dealId')} className={FIELD}>
            <option value="">(no deal)</option>
            {view.deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={PRIMARY}>
          {saving ? 'Saving...' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className={QUIET} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      <Refusals errors={errors} />
      {saved && (
        <p role="status" className="text-sm text-success">
          {saved}
        </p>
      )}
    </form>
  );
}
